import ky, { HTTPError } from 'ky';
import { ApiErrorSchema, type ApiError } from '@ctms/contracts';

import { clearAuthToken, getAuthToken } from '../features/auth/auth-token.js';

/**
 * Single HTTP client for the cockpit. In dev, Vite proxies `/api` to the
 * local NestJS app; in prod nginx routes `/api` to the api container.
 * Money ALWAYS travels as strings over the wire (ADR-003) — never JSON numbers.
 * Note: ky 2.x renamed `prefixUrl` to `prefix`.
 */
export const apiClient = ky.create({
  prefix: '/api/v1',
  timeout: 30_000,
  retry: {
    limit: 1,
    methods: ['get'],
    statusCodes: [408, 413, 429, 500, 502, 503, 504],
  },
  hooks: {
    beforeRequest: [
      ({ request }) => {
        const token = getAuthToken();
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
    ],
    afterResponse: [
      ({ response }) => {
        if (response.status === 401) {
          clearAuthToken();
        }
        return response;
      },
    ],
  },
});

/**
 * Safe parser for ApiError from ky HTTPError or generic errors.
 */
export async function parseApiError(error: unknown): Promise<ApiError | null> {
  if (error instanceof HTTPError) {
    // ky automatically parses error response data if .json() was called
    if (error.data) {
      const parsed = ApiErrorSchema.safeParse(error.data);
      if (parsed.success) {
        return parsed.data;
      }
    }
    try {
      const json: unknown = await error.response.clone().json();
      const parsed = ApiErrorSchema.safeParse(json);
      if (parsed.success) {
        return parsed.data;
      }
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Helper to extract a user-facing error message.
 */
export async function getApiErrorMessage(
  error: unknown,
  fallback = 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.',
): Promise<string> {
  const apiError = await parseApiError(error);
  if (apiError?.message) {
    return apiError.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}
